import React, { useEffect, useState, useRef } from 'react';
import * as api from '../../services/api';
import { SuppressionType, SuppressionEntry, SuppressionImportJob } from '../../services/api';

/**
 * SuppressionListShell
 *
 * Inline list-manager rendered inside VertebraConfigModal for the
 * email_suppression and phone_suppression vertebra types. Manages the
 * account-level suppression list (see migration 015) — not a body-local
 * config.
 *
 * Layout:
 *   1. Account-list notice (the "shared across all data bodies" callout)
 *   2. Header strip with running total + soft-warn pill
 *   3. Manual paste — up to 1,000 entries per submission
 *   4. CSV upload — up to 25,000 rows per file (queues a job)
 *   5. Search the list — type to look up entries; delete one at a time
 *
 * The Brain field picker and matching-rules block are rendered above this
 * component in VertebraConfigModal.tsx (mirrors the free_email_check /
 * free_phone_check pattern). The picker reads/writes `suppressionField`.
 *
 * The list is search-driven by design: with potentially millions of
 * entries per account, browsing pages of opaque values has no use case.
 * The running total + paste/CSV summaries cover "did my add land?". To
 * find or remove a specific entry, you search for it.
 */

const PAGE_SIZE = 50;
const PASTE_HARD_CAP = 1000;
const CSV_HARD_CAP_ROWS = 25000;

interface Props {
  type: SuppressionType;
}

export function SuppressionListShell({ type }: Props) {
  // --- counts (header strip) ---
  const [counts, setCounts] = useState<{ email: number; phone: number } | null>(null);
  const [softWarnTotal, setSoftWarnTotal] = useState<number>(1_000_000);
  const [countsLoading, setCountsLoading] = useState(true);

  // --- list search ---
  // `entries` / `entriesLoading` only carry meaning when `search` is non-empty.
  // With an empty search box we render an instructional empty state and skip
  // the entries query entirely (no point listing 10k rows nobody asked for).
  const [entries, setEntries] = useState<SuppressionEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  // --- paste form ---
  const [pasteText, setPasteText] = useState('');
  const [pasteStatus, setPasteStatus] = useState<string | null>(null);
  const [pasteBusy, setPasteBusy] = useState(false);

  // --- csv upload ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [importJob, setImportJob] = useState<SuppressionImportJob | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const label = type === 'email' ? 'Email' : 'Phone';
  const labelPlural = type === 'email' ? 'email addresses' : 'phone numbers';
  const placeholder = type === 'email'
    ? 'paste emails — one per line, or comma-separated\n\nexample@domain.com\nanother@domain.com, third@domain.com'
    : 'paste phone numbers — one per line, or comma-separated\n\n(555) 123-4567\n555-987-6543, 5551112222';

  // Load counts on mount and after any mutation.
  const refreshCounts = async () => {
    try {
      const r = await api.getSuppressionCounts();
      setCounts(r.counts);
      setSoftWarnTotal(r.softWarnTotal);
    } catch (err: any) {
      console.error('Failed to load suppression counts:', err);
    } finally {
      setCountsLoading(false);
    }
  };

  // Load a page of entries. No-op if the search box is empty — see the
  // search-driven note above. Called by both the search useEffect and by
  // paste/CSV/delete handlers; the latter want a refresh only if the user
  // is currently looking at search results.
  const refreshEntries = async () => {
    if (search.trim() === '') {return;}
    setEntriesLoading(true);
    try {
      const rows = await api.listSuppressionEntries(type, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        search: search.trim(),
      });
      setEntries(rows);
    } catch (err: any) {
      console.error('Failed to load entries:', err);
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  };

  useEffect(() => { void refreshCounts();   }, []);
  useEffect(() => {
    // Search-driven: don't fetch (or render) entries until the user types.
    if (search.trim() === '') {
      setEntries([]);
      setEntriesLoading(false);
      return;
    }
    void refreshEntries();
    // eslint-disable-next-line
  }, [type, page, search]);

  // Poll the running import job until terminal.
  useEffect(() => {
    if (!importJob || importJob.status === 'completed' || importJob.status === 'failed') {
      return;
    }
    const interval = setInterval(async () => {
      try {
        const fresh = await api.getSuppressionImportStatus(importJob.id);
        setImportJob(fresh);
        if (fresh.status === 'completed' || fresh.status === 'failed') {
          void refreshCounts();
          void refreshEntries();
        }
      } catch (err: any) {
        console.error('Import poll failed:', err);
      }
    }, 1500);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [importJob?.id, importJob?.status]);

  // --- paste handler ---
  const onPasteSubmit = async () => {
    setPasteStatus(null);
    if (!pasteText.trim()) {
      setPasteStatus('Nothing pasted yet.');
      return;
    }
    // Quick client-side count just so the user knows they're over before we round-trip.
    const tentative = pasteText.split(/[\n,;\t]/).filter(s => s.trim().length > 0);
    if (tentative.length > PASTE_HARD_CAP) {
      setPasteStatus(`Too many — paste up to ${PASTE_HARD_CAP.toLocaleString()} ${labelPlural} at a time. For larger lists, use CSV upload.`);
      return;
    }
    setPasteBusy(true);
    try {
      const r = await api.pasteSuppressionEntries(type, pasteText);
      setPasteText('');
      setPasteStatus(`Added ${r.inserted.toLocaleString()} new — ${r.duplicate.toLocaleString()} already on list, ${r.rejected.toLocaleString()} skipped (not a valid ${type}).`);
      void refreshCounts();
      void refreshEntries();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Add failed';
      setPasteStatus(`Error: ${msg}`);
    } finally {
      setPasteBusy(false);
    }
  };

  // --- csv upload handler ---
  const onFilePicked = async (file: File) => {
    setUploadError(null);
    setUploadProgress(0);
    try {
      const { jobId } = await api.uploadSuppressionCsv(type, file, (loaded, total) => {
        setUploadProgress(total > 0 ? Math.round((loaded / total) * 100) : 0);
      });
      setUploadProgress(null);
      // Kick off the polling effect by fetching the initial job state.
      const job = await api.getSuppressionImportStatus(jobId);
      setImportJob(job);
    } catch (err: any) {
      setUploadProgress(null);
      const msg = err.response?.data?.error || err.message || 'Upload failed';
      setUploadError(msg);
    } finally {
      if (fileInputRef.current) {fileInputRef.current.value = '';}
    }
  };

  // --- delete entry ---
  const onDeleteEntry = async (id: number) => {
    try {
      await api.deleteSuppressionEntry(id);
      void refreshCounts();
      void refreshEntries();
    } catch (err: any) {
      console.error('Delete failed:', err);
    }
  };

  const total = counts ? counts[type] : 0;
  const overSoftWarn = total >= softWarnTotal;

  return (
    <div style={{ marginTop: 8 }}>

      {/* --- 1. Account-list notice --- */}
      <div style={{
        background: '#eef2ff',
        border: '1px solid #c7d2fe',
        borderRadius: 6,
        padding: '10px 14px',
        fontSize: 13,
        color: '#312e81',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}>
        <span style={{ fontSize: 16, lineHeight: 1 }}>&#9432;</span>
        <div>
          <strong>This list is shared across every data body in your account.</strong>{' '}
          Edits here affect every spine that uses an {label} Suppression vertebra. Used
          for opt-outs, do-not-contact / do-not-call requests, and any {labelPlural}
          you&apos;ve agreed never to share or sell.
        </div>
      </div>

      {/* --- 2. Header strip with counts --- */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
      }}>
        <div style={{ fontSize: 14, color: '#374151' }}>
          {countsLoading ? (
            <span style={{
              display: 'inline-block',
              width: 120,
              height: 16,
              background: 'linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.4s linear infinite',
              borderRadius: 4,
            }} className="stat-skeleton" />
          ) : (
            <>
              <strong>{total.toLocaleString()}</strong>{' '}
              {total === 1 ? `${type === 'email' ? 'address' : 'number'}` : labelPlural} on list
            </>
          )}
        </div>
        {overSoftWarn && (
          <div style={{
            fontSize: 12,
            color: '#92400e',
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            padding: '4px 8px',
            borderRadius: 4,
          }}>
            Past {softWarnTotal.toLocaleString()} entries — still fine, just a heads-up.
          </div>
        )}
      </div>

      {/* --- 3. Manual paste --- */}
      <div className="form-group">
        <label className="form-label">
          Add entries — paste manually
        </label>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
          Up to {PASTE_HARD_CAP.toLocaleString()} {labelPlural} per submission. One per
          line, or comma-separated. We&apos;ll normalize and dedupe automatically.
        </div>
        <textarea
          value={pasteText}
          onChange={e => setPasteText(e.target.value)}
          rows={5}
          placeholder={placeholder}
          disabled={pasteBusy}
          style={{
            width: '100%',
            padding: 10,
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontFamily: 'monospace',
            fontSize: 13,
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 8,
        }}>
          <button
            type="button"
            onClick={onPasteSubmit}
            disabled={pasteBusy || pasteText.trim().length === 0}
            style={{
              padding: '6px 14px',
              border: 'none',
              borderRadius: 6,
              background: pasteBusy ? '#9ca3af' : '#4f46e5',
              color: '#fff',
              fontSize: 13,
              fontWeight: 500,
              cursor: pasteBusy || pasteText.trim().length === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {pasteBusy ? 'Adding…' : 'Add to list'}
          </button>
          {pasteStatus && (
            <span style={{ fontSize: 12, color: '#374151' }}>{pasteStatus}</span>
          )}
        </div>
      </div>

      {/* --- 4. CSV upload --- */}
      <div className="form-group" style={{ marginTop: 18 }}>
        <label className="form-label">
          Or upload a CSV
        </label>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
          Up to {CSV_HARD_CAP_ROWS.toLocaleString()} rows per file. One value per row
          (first column). Larger lists? Split them and upload in batches.
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) {void onFilePicked(f);}
          }}
          disabled={uploadProgress !== null || (importJob?.status === 'processing' || importJob?.status === 'queued')}
          style={{ fontSize: 13 }}
        />

        {/* Upload progress */}
        {uploadProgress !== null && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#374151' }}>
            Uploading… {uploadProgress}%
          </div>
        )}

        {/* Running import */}
        {importJob && (importJob.status === 'queued' || importJob.status === 'processing') && (
          <div style={{
            marginTop: 10,
            background: '#f8f9fb',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 12,
            color: '#374151',
          }}>
            <div><strong>Importing…</strong> {importJob.progressPct}%</div>
            <div style={{ marginTop: 4 }}>
              {importJob.rowsProcessed.toLocaleString()} processed
              {' · '}
              {importJob.rowsInserted.toLocaleString()} added
              {' · '}
              {importJob.rowsDuplicate.toLocaleString()} duplicates
              {importJob.rowsRejected > 0 && ` · ${importJob.rowsRejected.toLocaleString()} skipped`}
            </div>
          </div>
        )}

        {/* Completed import */}
        {importJob && importJob.status === 'completed' && (
          <div style={{
            marginTop: 10,
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 12,
            color: '#065f46',
          }}>
            <strong>Import complete.</strong>{' '}
            Added {importJob.rowsInserted.toLocaleString()} new
            {' · '}
            {importJob.rowsDuplicate.toLocaleString()} already on list
            {importJob.rowsRejected > 0 && ` · ${importJob.rowsRejected.toLocaleString()} skipped`}
            .
          </div>
        )}

        {/* Failed import */}
        {importJob && importJob.status === 'failed' && (
          <div style={{
            marginTop: 10,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 12,
            color: '#991b1b',
          }}>
            <strong>Import failed.</strong> {importJob.error || 'Unknown error.'}
          </div>
        )}

        {uploadError && (
          <div style={{
            marginTop: 10,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 12,
            color: '#991b1b',
          }}>
            {uploadError}
          </div>
        )}
      </div>

      {/* --- 5. Search the list --- */}
      {/* Search-driven: with potentially millions of entries per account,
          browsing pages of opaque values has no real use case. The empty
          state shows a hint instead of rendering 50 rows nobody asked
          for. Typing into the box flips into paginated results mode. */}
      <div className="form-group" style={{ marginTop: 24 }}>
        <label className="form-label">Search the list</label>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}>
          <input
            type="text"
            placeholder={`Search ${labelPlural}…`}
            value={search}
            onChange={e => { setPage(0); setSearch(e.target.value); }}
            style={{
              flex: 1,
              padding: '6px 10px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 13,
            }}
          />
        </div>

        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          maxHeight: 320,
          overflowY: 'auto',
        }}>
          {search.trim() === '' ? (
            <div style={{ padding: 16, fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
              Type above to find a specific {type === 'email' ? 'email address' : 'phone number'} on
              your list. Partial matches work too — e.g. <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 3 }}>{type === 'email' ? '@gmail.com' : '717'}</code>.
              <div style={{ marginTop: 6, fontSize: 12, color: '#9ca3af' }}>
                Total on list shown above. Per-entry removal happens from search results.
              </div>
            </div>
          ) : entriesLoading ? (
            <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>Loading…</div>
          ) : entries.length === 0 ? (
            <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>No matches.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{e.valueNorm}</td>
                    <td style={{ padding: '6px 12px', color: '#9ca3af', fontSize: 11, whiteSpace: 'nowrap' }}>
                      {e.source ?? ''}
                    </td>
                    <td style={{ padding: '6px 12px', width: 1 }}>
                      <button
                        type="button"
                        onClick={() => onDeleteEntry(e.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#dc2626',
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                        title="Remove from list"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pager — only relevant when search is active. With no search, the
            entries array is empty and there's nothing to page through. */}
        {search.trim() !== '' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 8,
            fontSize: 12,
            color: '#6b7280',
          }}>
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage(p => Math.max(0, p - 1))}
              style={{
                background: 'transparent',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                padding: '4px 10px',
                fontSize: 12,
                cursor: page === 0 ? 'not-allowed' : 'pointer',
                opacity: page === 0 ? 0.4 : 1,
              }}
            >
              Prev
            </button>
            <span>Page {page + 1}</span>
            <button
              type="button"
              disabled={entries.length < PAGE_SIZE}
              onClick={() => setPage(p => p + 1)}
              style={{
                background: 'transparent',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                padding: '4px 10px',
                fontSize: 12,
                cursor: entries.length < PAGE_SIZE ? 'not-allowed' : 'pointer',
                opacity: entries.length < PAGE_SIZE ? 0.4 : 1,
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

    </div>
  );
}

export default SuppressionListShell;
